type DuplicateMauiAppOptions =
	| {
			/** Pre-formed container ARI for any product (e.g. `ari:cloud:jira:<cloudId>:issue/<issueId>`). */
			containerAri: string;
	  }
	| {
			containerAri?: undefined;
			/** Confluence page or blog post ID used to build a Confluence ARI. */
			containerContentId: string;
			/** Cloud ID used to build `ari:cloud:confluence:<cloudId>:<contentType>/<containerContentId>`. */
			cloudId?: string;
			/** Content type of the container ('page' | 'blogpost'). Defaults to 'page'. */
			contentType?: string;
	  };

/**
 * Duplicates a MAUI app by calling the Remix duplicate API endpoint.
 *
 * Pass `{ containerAri }` when you already have a fully-formed ARI (e.g. a Jira issue ARI).
 * Pass `{ containerContentId, cloudId, contentType }` to let this function build a Confluence ARI.
 *
 * @param appId - The ID of the MAUI app to duplicate
 * @param options - Either a pre-formed `containerAri`, or Confluence-specific fields to build one
 * @returns The ID of the newly duplicated app
 */
export async function duplicateMauiApp(
	appId: string,
	options: DuplicateMauiAppOptions,
): Promise<{ id: string }> {
	let containerAri: string | undefined;
	if ('containerAri' in options) {
		containerAri = options.containerAri;
	} else {
		const ariContentType = options.contentType === 'blogpost' ? 'blogpost' : 'page';
		containerAri = options.cloudId
			? `ari:cloud:confluence:${options.cloudId}:${ariContentType}/${options.containerContentId}`
			: options.containerContentId;
	}

	const response = await fetch(
		`/gateway/api/remix/api/v1/ui/${encodeURIComponent(appId)}/duplicate`,
		{
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				containerAri,
			}),
		},
	);

	if (!response.ok) {
		throw new Error("We couldn't duplicate this component. Please try again.");
	}

	const data = await response.json();
	if (!data?.id) {
		throw new Error(
			"We couldn't duplicate this component. The server did not return a new component ID.",
		);
	}

	return { id: data.id };
}
